/* eslint-disable @typescript-eslint/camelcase */
import { Component, Vue } from 'vue-property-decorator'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import AppForm from '@core/components/AppForm.vue'
import { IDateService } from '@master/services/IDateService'
import { ClientDependant } from '@core/pages/client-management/model/ClientDependant'
import { FormStructure } from '@master/model/structure/FormStructure'
import { NationalityOptions } from '@master/model/options/NationalityAndCountryOptions'
import { namespace } from 'vuex-class'
import { RelationshipOptions } from '@master/model/options/RelationshipOptions'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import AppDialog from '@core/components/AppDialog.vue'
import { ClientDependantsService } from '@core/pages/client-management/api/services/ClientDependantsService'
import { RelationshipCorporateOptions } from '@master/model/options/RelationshipCorporateOptions'
import { ClientDto, ClientResponseDto, ClientService, PersonalInfo } from '@core/pages/client-management/api'

const clientInfoStore = namespace('ClientInfoStore')

interface Client {
  _oid: string;
  name: string;
  nric: string;
  mobile_number: string;
  status: string;
  client_since: string;
  account_type: string;
  redirect: string;
  keyword: string;
  client_id: string;
}

@Component({
  components: {
    AppForm,
    AppDialog
  }
})
export class ClientDependantsMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @clientInfoStore.State
  private clientDto: ClientDto

  @inject()
  protected $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  @clientInfoStore.State
  private readOnly

  protected dependants: ClientDependant[] = []
  protected depObj: ClientDependant = new ClientDependant()
  protected depObjIdx = -1
  protected showDepForm = false
  protected isNameDisabled = false

  private clientList: ClientResponseDto[] = []
  private clientListOptionsMap = []
  private isCorporate = false

  formattedDate (unixTime: number | string) {
    return this.dateService.format(Number(unixTime), 'YYYY-MM-DD')
  }

  get curUser () {
    return this.$store.getters.currentUser
  }

  get isSuperUser () {
    return this.curUser?.isSuperUser || false
  }

  get policyOid () {
    return this.$route.params.policyOid
  }

  get clientOid () {
    return this.$route.params.clientOid
  }

  get nationalityOptions () {
    return NationalityOptions
  }

  get clientListOptions () {
    return this.clientList.map(client => {
      return {
        label: `${client.name} (${client.clientId ? client.clientId : 'unknown'})`,
        value: client.oid
      }
    })
  }

  get dependantDisplayFields (): FormStructure<ClientDependant> {
    return [
      {
        label: 'Name',
        key: 'name'
      },
      {
        label: 'Relationship',
        key: 'relationshipDisplay'
      }
    ]
  }

  retrievePersonalInfoFromDep (dependant: ClientDependant) {
    return this.clientList.find(client => dependant.childClientOid === client.oid)?.personalInfoJson || {}
  }

  get dependantContactInfo (): FormStructure<PersonalInfo> {
    return [
      {
        label: 'Mobile No.',
        key: 'mobileNoOne'
      },
      {
        label: 'Office No.',
        key: 'officeNoOne'
      }
    ]
  }

  get dependantStructure (): FormStructure<ClientDependant> {
    return [
      {
        label: 'Name',
        key: 'childClientOid',
        options: this.clientListOptions,
        disabled: this.isNameDisabled,
        onChange: (oldVal, newVal) => {
          const clientObj = this.clientList.find((elem) => {
            return elem.oid === newVal
          })
          this.depObj.name = clientObj?.name
        }
      },
      {
        label: 'Relationship',
        key: 'relationship',
        options: RelationshipOptions,
        show: () => !this.isCorporate
      },
      {
        label: 'Relationship',
        key: 'relationship',
        type: 'multi',
        options: RelationshipCorporateOptions,
        show: () => this.isCorporate
      }
    ]
  }

  displayValue (prop, val) {
    if (prop.type === 'date') {
      return val ? this.formattedDate(val) : val
    } else {
      return val
    }
  }

  async fetchDependants () {
    const response = await ClientDependantsService.getDependantRelationshpList(this.clientOid)
    this.dependants = []
    response.forEach((dep) => {
      const newDep = new ClientDependant()
      Object.keys(dep).forEach(function (key) {
        newDep[key] = dep[key]
      })
      // parses the relationship as array
      newDep.relationship = JSON.parse(newDep.relationship?.toString()) as string[]
      this.dependants.push(newDep)
    })
  }

  renameObjectKeys (list: Array<object>, keys: Array<string>): Array<object> {
    const newList = list.map(function (data) {
      const keyValues = Object.keys(data).map(function (key) {
        const newKey = keys[key]
        return { [newKey]: data[key] }
      })
      return Object.assign({}, ...keyValues)
    })
    return newList
  }

  closeDialog () {
    this.showDepForm = false
    this.isNameDisabled = false
  }

  async fetchClientList () {
    this.clientList = await ClientService.retrieveClientList()
  }

  async addDependant (newDep: ClientDependant) {
    const inRelationship = this.isCorporate ? JSON.stringify(newDep.relationship) : JSON.stringify([newDep.relationship])
    await ClientDependantsService.saveNewDependantRelationship({
      clientOid: this.clientOid,
      childClientOid: newDep.childClientOid,
      relationship: inRelationship,
      relationshipData: JSON.stringify({}) // can be extended in the future in case the relationship requires more details
    })
  }

  async updateDependantObj (dep: ClientDependant) {
    const inRelationship = this.isCorporate ? JSON.stringify(dep.relationship) : JSON.stringify([dep.relationship])
    await ClientDependantsService.updateDependantRelationship({
      oid: dep.oid,
      childClientOid: dep.childClientOid,
      relationship: inRelationship
    })
  }

  async removeDependant (depOid) {
    await ClientDependantsService.removeDependant(depOid)
  }

  createDep () {
    this.depObjIdx = -1
    this.depObj = new ClientDependant()
    this.showDepForm = true
  }

  editDep (dep: ClientDependant, idx: number) {
    this.depObjIdx = idx
    Object.assign(this.depObj, dep)
    this.showDepForm = true
    this.isNameDisabled = true
  }

  async removeDep (depOid: string) {
    const isConfirm = await this.uiService.confirm.show({
      type: 'warning',
      message: 'Confirm to remove dependant ?'
    })
    if (isConfirm) {
      await this.removeDependant(depOid)
      await this.fetchDependants()
    }
  }

  // checks for existing relationships, and if found, calls the update endpoint instead of the create endpoint
  checkForExistingRelationship () {
    const found = this.dependants.find(elem => elem.childClientOid === this.depObj.childClientOid)
    console.log(found)
    if (found) {
      // as long as depObjIdx is not -1, its considered updating a new client
      this.depObjIdx = 0
      this.depObj.oid = found.oid
    }
  }

  async save () {
    this.checkForExistingRelationship()
    const saveTarget = Object.assign(new ClientDependant(), this.depObj)
    if (this.depObjIdx !== -1) { // edit existing dep
      await this.updateDependantObj(saveTarget)
    } else { // add new dep
      await this.addDependant(saveTarget)
    }
    this.showDepForm = false
    await this.fetchDependants()
  }

  checkAccountType () {
    this.isCorporate = this.clientDto?.accountType === 'corporate'
  }

  async mounted () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    await this.fetchDependants()
    this.fetchClientList()
    this.checkAccountType()
  }
}
